import {
  ActivityIndicator, Alert, Modal, Pressable, StyleSheet, ScrollView, Text, View,
  KeyboardAvoidingView, Platform, TextInput, Keyboard,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import React, {
  memo, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import Markdown from 'react-native-markdown-display';
import Moment from 'moment';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import Animated, { SharedValue, useAnimatedStyle } from 'react-native-reanimated';
import { FontAwesome } from '@expo/vector-icons';
import Style from '../../constants/Style';
import BotIcon60 from '../../components/60pxIcons/BotIcon60';
import Colors from '../../constants/Colors';
import getChatbotSessions from '../../requests/Chatbot/GetChatbotSessions';
import sendChatMessage from '../../requests/Chatbot/SendChatMessage';
import getSessionMessages from '../../requests/Chatbot/GetSessionMessages';
import isMobile from '../../components/IsMobile';
import RefreshIcon from '../../components/16pxIcons/RefreshIcon';
import BookmarkIcon from '../../components/16pxIcons/BookmarkIcon';
import BinIcon from '../../components/BinIcon';
import EditIcon24 from '../../components/24pxIcons/EditIcon24';
import archiveChatbotSession from '../../requests/Chatbot/ArchiveChatbotSession';
import BinIcon12 from '../../components/12pxIcons/BinIcon12';
import deleteChatbotSession from '../../requests/Chatbot/DeleteChatbotSession';
import AlertModal from '../../components/AlertModal';
import unarchiveChatbotSession from '../../requests/Chatbot/UnarchiveChatbotSession';
import updateChatSessionName from '../../requests/Chatbot/UpdateChatSessionName';
import LineGraph from '../../graphs/LineGraph';
import BarGraph from '../../graphs/BarGraph';
import ChildrenBarGraph from '../../graphs/ChildrenBarGraph';
import LineGraphMobile from '../../graphs/LineGraphMobile';
import PieChart from '../../graphs/PieChart';
import ChatbotTextInput from './ChatbotTextInput';
import ChildrenBarGraphWeb from '../../graphs/ChildrenBarGraphWeb';
import ClockCounterClockwise from '../../components/24pxIcons/ClockCounterClockwise';
import bookmarkChatbotSession from '../../requests/Chatbot/BookmarkChatbotSession';

export default function ChatbotScreen() {
  const [activeSessions, setActiveSessions] = useState([]);
  const [activeSessionsPage, setActiveSessionsPage] = useState(1);
  const [activeSessionsTotalPages, setActiveSessionsTotalPages] = useState(1);
  const [archivedSessions, setArchivedSessions] = useState([]);
  const [archivedSessionsPage, setArchivedSessionsPage] = useState(1);
  const [archivedSessionsTotalPages, setArchivedSessionsTotalPages] = useState(1);
  const [savedSessions, setSavedSessions] = useState([]);
  const [savedSessionsPage, setSavedSessionsPage] = useState(1);
  const [savedSessionsTotalPages, setSavedSessionsTotalPages] = useState(1);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotalPages, setMessagesTotalPages] = useState(1);
  const [reloadCount, setReloadCount] = useState(0);
  const [loadingActiveSessions, setLoadingActiveSessions] = useState(false);
  const [loadingArchivedSessions, setLoadingArchivedSessions] = useState(false);
  const [loadingSavedSessions, setLoadingSavedSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [LastMessageSent, setLastMessageSent] = useState(Moment());
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatHistoryModalVisible, setChatHistoryModalVisible] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [archivingSession, setArchivingSession] = useState(false);
  const [bookmarkingSession, setBookmarkingSession] = useState(false);
  const [deletingSession, setDeletingSession] = useState(false);
  const [editingSessionName, setEditingSessionName] = useState(false);
  const [sessionNameDraft, setSessionNameDraft] = useState('');
  const [updatingSessionName, setUpdatingSessionName] = useState(false);
  const [chatHistoryExpanded, setChatHistoryExpanded] = useState(true);
  const [archivedChatsExpanded, setArchivedChatsExpanded] = useState(true);
  const [savedSessionsExpanded, setSavedSessionsExpanded] = useState(true);

  const scrollViewRef = useRef<any>(null);

  const buildingId = useSelector(
    (state: { selectedBuilding: { id: string } }) => state.selectedBuilding.id,
  );

  useEffect(() => {
    const fetchChatbotSessions = async (
      isArchived: boolean,
      isBookmarked: boolean,
      page: number,
      setLoading: React.Dispatch<React.SetStateAction<boolean>>,
      setSessionData: React.Dispatch<React.SetStateAction<any[]>>,
      setTotalPages: React.Dispatch<React.SetStateAction<number>>,
      setPage: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      setLoading(true);
      const response = await getChatbotSessions(isArchived, isBookmarked, page);
      if (Array.isArray(response)) {
        setSessionData(response.map((session: any) => ({
          ...session,
          isArchived,
        })));
        setTotalPages(Math.max(1, Math.ceil(response.length / 10)));
      } else {
        const items = response?.items ?? [];
        const count = typeof response?.count === 'number' ? response.count : items.length;
        const calculatedTotalPages = Math.max(1, Math.ceil(count / 10));
        setSessionData(items.map((session: any) => ({
          ...session,
          isArchived,
        })));
        setTotalPages(calculatedTotalPages);
        if (page > calculatedTotalPages) {
          setPage(calculatedTotalPages);
        }
      }
      setLoading(false);
    };
    fetchChatbotSessions(
      false,
      false,
      activeSessionsPage,
      setLoadingActiveSessions,
      setActiveSessions,
      setActiveSessionsTotalPages,
      setActiveSessionsPage,
    );
    fetchChatbotSessions(
      true,
      false,
      archivedSessionsPage,
      setLoadingArchivedSessions,
      setArchivedSessions,
      setArchivedSessionsTotalPages,
      setArchivedSessionsPage,
    );
    fetchChatbotSessions(
      false,
      true,
      savedSessionsPage,
      setLoadingSavedSessions,
      setSavedSessions,
      setSavedSessionsTotalPages,
      setSavedSessionsPage,
    );
  }, [reloadCount, activeSessionsPage, archivedSessionsPage, savedSessionsPage]);

  useEffect(() => {
    if (selectedSession) {
      setLoadingMessages(true);
      setErrorMessage('');
      getSessionMessages(selectedSession.sessionId, messagesPage).then((messages) => {
        if (Array.isArray(messages)) {
          setSelectedSession({ ...selectedSession, messages });
          setMessagesTotalPages(Math.max(1, Math.ceil(messages.length / 10)));
        } else {
          const items = messages?.items ?? [];
          const count = typeof messages?.count === 'number' ? messages.count : items.length;
          const calculatedTotalPages = Math.max(1, Math.ceil(count / 10));
          setSelectedSession({ ...selectedSession, messages: items });
          setMessagesTotalPages(calculatedTotalPages);
          if (messagesPage > calculatedTotalPages) {
            setMessagesPage(calculatedTotalPages);
          }
        }
        setLoadingMessages(false);
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
      });
    }
  }, [selectedSession?.sessionId, LastMessageSent, messagesPage]);

  useEffect(() => {
    setMessagesPage(1);
  }, [selectedSession?.sessionId]);

  useEffect(() => {
    setEditingSessionName(false);
    setSessionNameDraft('');
  }, [selectedSession?.sessionId]);

  function RightAction(prog: SharedValue<number>, drag: SharedValue<number>) {
    const styleAnimation = useAnimatedStyle(() => ({
      transform: [{ translateX: drag.value + 30 }],
    }));

    return (
      <Animated.View style={styleAnimation}>
        <Pressable
          onPress={() => Alert.alert('Coming soon!')}
          style={{
            width: 30, height: 40, justifyContent: 'center', alignItems: 'center',
          }}>
          <BinIcon />
        </Pressable>
      </Animated.View>
    );
  }

  const handleSendMessage = async ({ message }: { message: string }) => {
    if (message.trim() === '') return;
    Keyboard.dismiss();
    setSendingMessage(true);
    setErrorMessage('');
    sendChatMessage(message, buildingId, selectedSession?.sessionId).then((session) => {
      if (session.sessionId && selectedSession?.sessionId
        !== session.sessionId) {
        setSelectedSession({ ...session, isArchived: false });
        setReloadCount(reloadCount + 1);
      } else setLastMessageSent(Moment());
    }).catch(() => {
      setErrorMessage('Something went wrong while sending your message. Please try again.');
    }).finally(() => {
      setSendingMessage(false);
    });
  };

  const handleSessionNameUpdate = async () => {
    if (!selectedSession?.sessionId) return;
    const trimmedName = sessionNameDraft.trim();
    if (!trimmedName) {
      Alert.alert('Session name is required.');
      return;
    }
    if (trimmedName === selectedSession.sessionName) {
      setEditingSessionName(false);
      return;
    }
    setUpdatingSessionName(true);
    try {
      await updateChatSessionName(selectedSession.sessionId, trimmedName);
      setSelectedSession((prev: any) => (prev ? { ...prev, sessionName: trimmedName } : prev));
      setActiveSessions((prev: any[]) => prev.map((session: any) => (
        session.sessionId === selectedSession.sessionId
          ? { ...session, sessionName: trimmedName }
          : session
      )));
      setArchivedSessions((prev: any[]) => prev.map((session: any) => (
        session.sessionId === selectedSession.sessionId
          ? { ...session, sessionName: trimmedName }
          : session
      )));
      setEditingSessionName(false);
    } catch {
      Alert.alert('Failed to update session name. Please try again.');
    } finally {
      setUpdatingSessionName(false);
    }
  };

  const Sessions = ({
    title,
    icon,
    sessions,
    loading,
    expanded,
    onToggle,
    currentPage,
    totalPages,
    onPageChange,
  }: {
    title: string;
    icon?: React.ReactNode;
    sessions: any[];
    loading: boolean;
    expanded: boolean;
    onToggle: () => void;
    currentPage: number;
    totalPages: number;
    onPageChange: React.Dispatch<React.SetStateAction<number>>;
  }) => {
    const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
    const expandedContentMaxHeight = isMobile ? 320 : 420;
    const paginationHeight = totalPages > 1 ? 60 : 0;
    const sessionListMaxHeight = Math.max(120, expandedContentMaxHeight - paginationHeight - 24);
    const sectionSlug = title.toLowerCase().replace(/\s+/g, '-');

    return (
      <View style={{
        borderWidth: 1,
        borderColor: Colors.light.hereworksWhite300,
      }}>
        <Pressable
          testID={`chatbot-${sectionSlug}-toggle`}
          onPress={onToggle}
          style={{
            paddingHorizontal: 12,
            paddingVertical: 12,
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: expanded ? Colors.light.plainBackground : 'white',
          }}>
          {icon ? <View style={{ marginRight: 8 }}>
            {icon}
          </View> : null}
          <Text style={{
            flex: 1,
            fontSize: 12,
            lineHeight: 20,
            fontFamily: 'Poppins_500Medium',
            color: Colors.light.hereworksBlack500,
          }}>{title}</Text>
          <FontAwesome
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={12}
            color={Colors.light.hereworksBlack400}
          />
        </Pressable>
        {!expanded ? null : <View style={{ maxHeight: expandedContentMaxHeight, padding: 12 }}>
          {loading ? <Text>Loading...</Text> : null}
          {!loading && sessions?.length === 0 ? <Text>No chat sessions found.</Text> : null}
          {!loading ? <ScrollView
            style={{ maxHeight: sessionListMaxHeight }}
            nestedScrollEnabled
            showsVerticalScrollIndicator
            testID={`chatbot-${sectionSlug}-list`}
          >
            {sessions.map((session: any) => (
              <Swipeable
                renderRightActions={RightAction}
                enabled={isMobile}
                key={session.sessionId}
              >
                <Pressable
                  testID={`chatbot-session-${session.sessionId}`}
                  key={session.sessionId}
                  disabled={selectedSession?.sessionId === session.sessionId}
                  onPress={() => {
                    setSelectedSession(session);
                    setChatHistoryModalVisible(false);
                  }}
                  style={{
                    padding: 10,
                    borderWidth: 1,
                    borderColor: '#eee',
                    borderRadius: 8,
                    marginBottom: 10,
                    backgroundColor: selectedSession?.sessionId === session.sessionId
                      ? Colors.light.hereworksBlue500 : 'white',
                  }}>
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: 'Poppins_400Regular',
                      color: selectedSession?.sessionId === session.sessionId
                        ? '#fff' : Colors.light.hereworksBlack500,
                    }}>{session.sessionName}</Text>
                </Pressable>
              </Swipeable>
            ))}
          </ScrollView> : null}
          {totalPages > 1 ? <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: 8,
            marginBottom: 12,
          }}>
            <Pressable
              testID={`chatbot-${sectionSlug}-prev-page`}
              disabled={currentPage === 1}
              onPress={() => onPageChange((prev) => Math.max(1, prev - 1))}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: currentPage === 1
                  ? Colors.light.hereworksWhite300
                  : Colors.light.hereworksBlue500,
                opacity: currentPage === 1 ? 0.5 : 1,
                marginHorizontal: 4,
                marginVertical: 4,
              }}>
              <Text style={{
                color: Colors.light.hereworksBlue500,
                fontFamily: 'Poppins_500Medium',
              }}>Prev</Text>
            </Pressable>
            {pages.map((page) => (
              <Pressable
                testID={`chatbot-${sectionSlug}-page-${page}`}
                key={page}
                onPress={() => onPageChange(page)}
                style={{
                  minWidth: 34,
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: page === currentPage
                    ? Colors.light.hereworksBlue500
                    : Colors.light.hereworksWhite300,
                  backgroundColor: page === currentPage
                    ? Colors.light.hereworksBlue500
                    : 'white',
                  marginHorizontal: 4,
                  marginVertical: 4,
                }}>
                <Text style={{
                  textAlign: 'center',
                  color: page === currentPage ? '#fff' : Colors.light.hereworksBlack500,
                  fontFamily: 'Poppins_500Medium',
                }}>{page}</Text>
              </Pressable>
            ))}
            <Pressable
              testID={`chatbot-${sectionSlug}-next-page`}
              disabled={currentPage === totalPages}
              onPress={() => onPageChange((prev) => Math.min(totalPages, prev + 1))}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: currentPage === totalPages
                  ? Colors.light.hereworksWhite300
                  : Colors.light.hereworksBlue500,
                opacity: currentPage === totalPages ? 0.5 : 1,
                marginHorizontal: 4,
                marginVertical: 4,
              }}>
              <Text style={{
                color: Colors.light.hereworksBlue500,
                fontFamily: 'Poppins_500Medium',
              }}>Next</Text>
            </Pressable>
          </View> : null}
        </View>}
      </View>
    );
  };

  type ChartData =
    | Array<{ value: number; timestamp?: string; date?: string; label?: string }>
    | {
      items?: Array<{
        id: string;
        name: string;
        description?: string;
        entityType?: string;
      }>;
    }
    | { line1?: { lineChartData?: Array<{ value: number; date: string }> } }
    | {
      bar1?: Array<{ value: number; label?: string }>;
      bar2?: Array<{ value: number; label?: string }>;
    };

  const Chart = memo(({ data, type }: { data: ChartData; type: string }) => {
    let chartData = [];
    if (Array.isArray(data)) {
      chartData = data.map((item, index) => ({
        index,
        value: item.value,
        date: item.timestamp ?? item.date,
      }));
    } else if ((data as any)?.line1?.lineChartData) {
      chartData = (data as any).line1.lineChartData.map((item: any, index: number) => ({
        index,
        value: item.value,
        date: item.date,
      }));
    }

    if (type === 'BAR_CHART') {
      let barChartData = [];
      if (Array.isArray(data)) {
        barChartData = data.map((item) => ({
          assetName: item.label,
          templateName: '',
          location: '',
          count: item.value,
        }));
      }
      if (isMobile) {
        return <ScrollView horizontal>
          <ChildrenBarGraph
            data={barChartData}
            compareToggled={false}
            comparisonData={[]} />
        </ScrollView>;
      }
      return <ChildrenBarGraphWeb data={barChartData} comparisonData={[]} compareToggled={false} />;
    }
    if (type === 'LINE_CHART') {
      if (isMobile) {
        return <View style={{ aspectRatio: 1 }}>
          <LineGraphMobile
            data={Array.isArray(data)
              ? data.map((point, index: number) => ({
                value: point.value,
                date: point.timestamp ?? point.date ?? '',
                index,
              }))
              : []}
            comparisonData={[]} />
        </View>;
      }
      return (<LineGraph chartData={chartData} noTitle noAxisLabel title='Value' />);
    }
    if (type === 'PIE_CHART') {
      return (
        <PieChart
          chartData={
            Array.isArray(data)
              ? data.map((item, index) => ({
                index,
                value: item.value,
                date: item.timestamp ?? item.date ?? '',
                label: item.label ?? '',
              }))
              : []
          }
        />
      );
    }

    if (type === 'COMPARISON_LINE_CHART') {
      let line1Data: { index: number; value: number; date: string }[] = [];
      let line2Data: { index: number; value: number; date: string }[] = [];
      if (
        typeof data === 'object'
        && data !== null
        && 'line1' in data
        && (data as any).line1?.lineChartData
      ) {
        line1Data = (data as any).line1.lineChartData.map((item: any, index: number) => ({
          index,
          value: item.value,
          date: item.timestamp ?? item.date ?? '',
        }));
      }
      if (
        typeof data === 'object'
        && data !== null
        && 'line2' in data
        && (data as any).line2?.lineChartData
      ) {
        line2Data = (data as any).line2.lineChartData.map((item: any, index: number) => ({
          index,
          value: item.value,
          date: item.timestamp ?? item.date ?? '',
        }));
      }

      if (isMobile) {
        return <View style={{ aspectRatio: 1 }}>
          <LineGraphMobile
            data={line1Data ?? []}
            comparisonData={line2Data ?? []} />
        </View>;
      }
      return (<LineGraph
        chartData={line1Data}
        comparisonData={line2Data ?? []}
        noTitle
        noAxisLabel
        title='Value'
      />);
    }
    if (type === 'COMPARISON_BAR_CHART') {
      let bar1Data = [];
      let bar2Data = [];
      if (
        typeof data === 'object'
        && data !== null
        && 'bar1' in data
        && Array.isArray((data as any).bar1?.barChartData)
      ) {
        bar1Data = (data as any).bar1.barChartData.map((item: any) => ({
          assetName: item.label,
          templateName: '',
          location: '',
          count: item.value,
        }));
      }
      if (
        typeof data === 'object'
        && data !== null
        && 'bar2' in data
        && Array.isArray((data as any).bar2?.barChartData)
      ) {
        bar2Data = (data as any).bar2.barChartData.map((item: any) => ({
          assetName: item.label,
          templateName: '',
          location: '',
          count: item.value,
        }));
      }
      if (isMobile) {
        return <ScrollView horizontal>
          <ChildrenBarGraph
            data={bar1Data}
            compareToggled={false}
            comparisonData={bar2Data} />
        </ScrollView>;
      }
      return <BarGraph
        chartData={bar1Data}
        comparisonData={bar2Data}
        title='Value'
      />;
    }

    if (type === 'ENTITY_LIST') {
      const entityItems = (
        typeof data === 'object'
        && data !== null
        && 'items' in data
        && Array.isArray((data as any).items)
      ) ? (data as any).items : [];

      if (!entityItems.length) {
        return <Text style={{
          fontFamily: 'Poppins_400Regular',
          color: Colors.light.hereworksBlack400,
          marginTop: 8,
        }}>
          {'No items found.'}
        </Text>;
      }

      return <ScrollView
        horizontal
        showsHorizontalScrollIndicator
        persistentScrollbar
        indicatorStyle='black'
        contentContainerStyle={{ paddingBottom: 6 }}
      >
        <View style={{
          marginTop: 10,
          borderWidth: 1,
          borderColor: Colors.light.hereworksWhite300,
          borderRadius: 8,
          overflow: 'hidden',
          minWidth: isMobile ? 520 : 640,
        }}>
          <View style={{
            flexDirection: 'row',
            backgroundColor: Colors.light.hereworksWhite300,
            borderBottomWidth: 1,
            borderBottomColor: Colors.light.hereworksWhite300,
          }}>
            <Text style={{
              flex: 1.2,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_500Medium',
              fontSize: 12,
              color: Colors.light.hereworksBlack500,
            }}>{'Name'}</Text>
            <Text style={{
              flex: 0.8,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_500Medium',
              fontSize: 12,
              color: Colors.light.hereworksBlack500,
            }}>{'Type'}</Text>
            <Text style={{
              flex: 2,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_500Medium',
              fontSize: 12,
              color: Colors.light.hereworksBlack500,
            }}>{'Description'}</Text>
            <Text style={{
              flex: 2,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_500Medium',
              fontSize: 12,
              color: Colors.light.hereworksBlack500,
            }}>{'ID'}</Text>
          </View>
          {entityItems.map((item: any, index: number) => <View
            key={item.id ?? `${item.name}-${index}`}
            style={{
              flexDirection: 'row',
              borderBottomWidth: index === entityItems.length - 1 ? 0 : 1,
              borderBottomColor: Colors.light.hereworksWhite300,
              backgroundColor: index % 2 === 0 ? 'white' : Colors.light.plainBackground,
            }}>
            <Text style={{
              flex: 1.2,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_400Regular',
              fontSize: 12,
              color: Colors.light.hereworksBlack500,
            }}>{item.name || '-'}</Text>
            <Text style={{
              flex: 0.8,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_400Regular',
              fontSize: 12,
              color: Colors.light.hereworksBlack500,
            }}>{item.entityType || '-'}</Text>
            <Text style={{
              flex: 2,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_400Regular',
              fontSize: 12,
              color: Colors.light.hereworksBlack500,
            }}>{item.description || '-'}</Text>
            <Text style={{
              flex: 2,
              paddingVertical: 8,
              paddingHorizontal: 10,
              fontFamily: 'Poppins_400Regular',
              fontSize: 12,
              color: Colors.light.hereworksBlack400,
            }}>{item.id || '-'}</Text>
          </View>)}
        </View>
      </ScrollView>;
    }

    return null;
  });

  return <KeyboardAvoidingView
    style={{ flex: 1 }}
    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0}
  >
    <View style={[
      Style.row,
      { flex: 1, marginHorizontal: isMobile ? 24 : 40, marginVertical: isMobile ? 18 : 32 },
    ]}>
      <View style={{
        flex: 3,
        backgroundColor: 'white',
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        borderRadius: 10,
      }}>
        {isMobile ? <View style={Style.row}>
          <Pressable testID='chatbot-open-history' onPress={() => setChatHistoryModalVisible(true)} style={{
            padding: 8,
            flexDirection: 'row',
            alignItems: 'center',
          }}>
            <RefreshIcon />
            <Text style={{
              color: Colors.light.hereworksBlue500,
              fontFamily: 'Poppins_500Medium',
              marginLeft: 4,
            }}>Chat History</Text>
          </Pressable>
          {selectedSession ? <Pressable
            testID='chatbot-new-session-mobile'
            style={{
              marginLeft: 'auto', padding: 8, flexDirection: 'row', alignItems: 'center',
            }}
            onPress={() => {
              setSelectedSession(null);
              setChatHistoryModalVisible(false);
            }}>
            <EditIcon24 />
            <Text style={{
              color: Colors.light.hereworksBlue500,
              fontFamily: 'Poppins_500Medium',
              marginLeft: 4,
            }}>New session</Text>
          </Pressable> : null}
        </View> : null}
        {isMobile ? null : <View style={{
          borderBottomWidth: 1,
          borderBottomColor: Colors.light.hereworksWhite200,
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: 21,
        }}>
          <View style={{
            paddingHorizontal: 20,
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
          }}>
            {editingSessionName ? <TextInput
              testID='chatbot-session-name-input'
              autoFocus
              value={sessionNameDraft}
              onChangeText={setSessionNameDraft}
              onSubmitEditing={handleSessionNameUpdate}
              editable={!updatingSessionName}
              placeholder='Session name'
              style={{
                flex: 1,
                borderWidth: 1,
                borderColor: Colors.light.hereworksWhite300,
                borderRadius: 8,
                paddingHorizontal: 10,
                paddingVertical: 8,
                fontFamily: 'Poppins_400Regular',
                fontSize: 18,
                marginRight: 12,
              }}
            /> : <Text style={{
              fontFamily: 'Poppins_400Regular',
              fontSize: 18,
              marginRight: 8,
            }}>{selectedSession?.sessionName ?? 'New Chat'}</Text>}
            {selectedSession && !editingSessionName ? <Pressable
              testID='chatbot-edit-session-name'
              onPress={() => {
                setSessionNameDraft(selectedSession.sessionName || '');
                setEditingSessionName(true);
              }}
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: 8,
              }}>
              <EditIcon24 />
            </Pressable> : null}
            {selectedSession && editingSessionName ? <View style={{
              flexDirection: 'row',
              alignItems: 'center',
            }}>
              <Pressable
                testID='chatbot-save-session-name'
                disabled={updatingSessionName}
                onPress={handleSessionNameUpdate}
                style={{
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  borderRadius: 6,
                  backgroundColor: Colors.light.hereworksBlue500,
                  marginRight: 8,
                  opacity: updatingSessionName ? 0.5 : 1,
                }}>
                <Text style={{
                  color: '#fff',
                  fontFamily: 'Poppins_500Medium',
                  fontSize: 12,
                }}>Save</Text>
              </Pressable>
              <Pressable
                testID='chatbot-cancel-session-name'
                disabled={updatingSessionName}
                onPress={() => {
                  setEditingSessionName(false);
                  setSessionNameDraft('');
                }}
                style={{
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: Colors.light.hereworksWhite300,
                  opacity: updatingSessionName ? 0.5 : 1,
                }}>
                <Text style={{
                  color: Colors.light.hereworksBlack500,
                  fontFamily: 'Poppins_500Medium',
                  fontSize: 12,
                }}>Cancel</Text>
              </Pressable>
            </View> : null}
          </View>
          {selectedSession ? <>
            <Pressable
              testID='chatbot-toggle-bookmark'
              style={{
                paddingHorizontal: 12,
                paddingVertical: 7,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: Colors.light.hereworksBlue500,
                marginRight: 12,
                justifyContent: 'center',
                alignItems: 'center',
              }}
              disabled={bookmarkingSession || archivingSession}
              onPress={() => {
                setBookmarkingSession(true);
                bookmarkChatbotSession(
                  selectedSession.sessionId,
                  selectedSession.isBookmarked,
                ).then(() => {
                  setSelectedSession(null);
                  setReloadCount(reloadCount + 1);
                  setBookmarkingSession(false);
                });
              }}>
              <BookmarkIcon />
              {bookmarkingSession ? <View
                style={[
                  StyleSheet.absoluteFillObject,
                  {
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#00000020',
                    borderRadius: 8,
                  },
                ]}>
                <ActivityIndicator
                  style={{}}
                  size="small"
                  color={Colors.light.hereworksBlue500}
                /></View> : null}
            </Pressable>
            <Pressable
              testID='chatbot-toggle-archive'
              style={{
                paddingHorizontal: 12,
                paddingVertical: 7,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: Colors.light.hereworksBlue500,
                marginRight: 12,
              }}
              disabled={archivingSession || bookmarkingSession}
              onPress={() => {
                setArchivingSession(true);
                if (selectedSession?.isArchived) {
                  unarchiveChatbotSession(selectedSession.sessionId).then(() => {
                    setSelectedSession(null);
                    setReloadCount(reloadCount + 1);
                    setArchivingSession(false);
                  });
                } else {
                  archiveChatbotSession(selectedSession.sessionId).then(() => {
                    setSelectedSession(null);
                    setReloadCount(reloadCount + 1);
                    setArchivingSession(false);
                  });
                }
              }}>
              <FontAwesome name="archive" size={16} color={Colors.light.hereworksBlue500} />
              {archivingSession ? <View
                style={[
                  StyleSheet.absoluteFillObject,
                  {
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#00000020',
                    borderRadius: 8,
                  },
                ]}>
                <ActivityIndicator
                  style={{}}
                  size="small"
                  color={Colors.light.hereworksBlue500}
                /></View> : null}
            </Pressable>
            {selectedSession?.isArchived && <Pressable
              testID='chatbot-delete-session'
              style={{
                paddingHorizontal: 12,
                paddingVertical: 7,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: Colors.light.hereworksRed300,
                marginRight: 12,
              }} onPress={() => {
                setDeletingSession(true);
              }}>
              <BinIcon12 />
            </Pressable>}
            <Pressable
              testID='chatbot-new-session'
              style={{
                paddingHorizontal: 12,
                paddingVertical: 7,
                borderRadius: 8,
                marginRight: 24,
                backgroundColor: Colors.light.hereworksBlue500,
              }}
              onPress={() => {
                setSelectedSession(null);
                setErrorMessage('');
                setChatHistoryModalVisible(false);
              }}>
              <Text style={{
                color: '#fff',
                fontSize: 14,
                lineHeight: 18,
                fontFamily: 'Roboto_400Regular',
              }}>New Session</Text>
            </Pressable></> : null}
        </View>}
        {selectedSession ? <>
          <ScrollView
            testID='chatbot-messages-list'
            ref={scrollViewRef}
            style={{
              flex: 1,
              marginBottom: 20,
              padding: isMobile ? 12 : 20,
            }}
            contentContainerStyle={{
              flexGrow: 1, paddingBottom: 20, justifyContent: 'flex-end',
            }}>
            {selectedSession.messages?.length === 0
              && <Text>No messages found.</Text>}
            {(selectedSession.messages ?? []).sort((a, b) => new Date(a.timestamp).getTime()
              - new Date(b.timestamp).getTime()).map((msg: any, index: number) => (
                <View key={index} style={{ marginBottom: 12, alignSelf: 'stretch' }}>
                  <View
                    testID='users-message'
                    style={{
                      borderWidth: 1,
                      borderColor: Colors.light.hereworksWhite300,
                      padding: 8,
                      borderRadius: 8,
                      marginLeft: '20%',
                      marginBottom: 16,
                    }}>
                    <View style={{ flexDirection: 'row', marginBottom: 4, alignItems: 'baseline' }}>
                      <Text style={{
                        fontFamily: 'Poppins_600SemiBold',
                        fontSize: 10,
                      }}>You</Text>
                      <Text style={{
                        fontSize: 10,
                        color: Colors.light.hereworksBlack400,
                        marginLeft: 8,
                      }}>{Moment(msg?.timestamp).format('DD MMM YYYY, h:mm A')}</Text>
                    </View>
                    <Text style={{
                      fontFamily: 'Poppins_400Regular',
                    }}>{msg?.userMessage}</Text>
                  </View>
                  <View
                    testID='agent-message'
                    style={{
                      marginRight: '20%',
                      backgroundColor: Colors.light.hereworksWhite300,
                      padding: 8,
                      borderRadius: 8,
                    }}>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: 4 }}>
                      <Text style={{
                        fontFamily: 'Poppins_600SemiBold',
                        fontSize: 10,
                      }}>Insights Agent</Text>
                      <Text style={{
                        fontSize: 10,
                        color: Colors.light.hereworksBlack400,
                        marginLeft: 8,
                      }}>{Moment(msg?.timestamp).format('DD MMM YYYY, h:mm A')}</Text>
                    </View>
                    <Markdown>{msg?.chatbotResponse?.message}</Markdown>
                    {(Array.isArray(msg.chatbotResponse?.uiWidgetMetaData?.data)
                      || Array.isArray(msg.chatbotResponse?.uiWidgetMetaData?.data?.line1
                        ?.lineChartData)
                      || (msg.chatbotResponse?.uiWidgetMetaData?.widgetType === 'ENTITY_LIST'
                        && Array.isArray(msg.chatbotResponse?.uiWidgetMetaData?.data?.items)))
                      ? <Chart data={msg.chatbotResponse.uiWidgetMetaData.data}
                        type={msg.chatbotResponse.uiWidgetMetaData.widgetType} /> : null}
                  </View>
                </View>))}
          </ScrollView>
          {messagesTotalPages > 1 ? <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: -8,
            marginBottom: 8,
          }}>
            <Pressable
              testID='chatbot-messages-prev-page'
              disabled={messagesPage === 1}
              onPress={() => setMessagesPage((prev) => Math.max(1, prev - 1))}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: messagesPage === 1
                  ? Colors.light.hereworksWhite300
                  : Colors.light.hereworksBlue500,
                opacity: messagesPage === 1 ? 0.5 : 1,
                marginHorizontal: 4,
              }}>
              <Text style={{
                color: Colors.light.hereworksBlue500,
                fontFamily: 'Poppins_500Medium',
              }}>{'Prev'}</Text>
            </Pressable>
            <Text style={{
              marginHorizontal: 8,
              fontFamily: 'Poppins_400Regular',
              color: Colors.light.hereworksBlack500,
            }}>{`${messagesPage} / ${messagesTotalPages}`}</Text>
            <Pressable
              testID='chatbot-messages-next-page'
              disabled={messagesPage === messagesTotalPages}
              onPress={() => setMessagesPage((prev) => Math.min(messagesTotalPages, prev + 1))}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: messagesPage === messagesTotalPages
                  ? Colors.light.hereworksWhite300
                  : Colors.light.hereworksBlue500,
                opacity: messagesPage === messagesTotalPages ? 0.5 : 1,
                marginHorizontal: 4,
              }}>
              <Text style={{
                color: Colors.light.hereworksBlue500,
                fontFamily: 'Poppins_500Medium',
              }}>{'Next'}</Text>
            </Pressable>
          </View> : null}
        </> : <ScrollView
          testID='chat-modal'
          style={{

          }} contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <LinearGradient
            colors={['#2BC3A510', '#85E1CF10']}
            style={{ padding: 13, borderRadius: 76 }}
          >
            <LinearGradient
              colors={['#2BC3A550', '#85E1CF50']}
              style={{ padding: 13, borderRadius: 76 }}
            >
              <LinearGradient
                colors={['#2BC3A5', '#85E1CF']}
                style={{ padding: 20, borderRadius: 50 }}
              >
                <BotIcon60 />
              </LinearGradient>
            </LinearGradient>
          </LinearGradient>
          <Text style={{
            fontFamily: 'Poppins_400Regular',
            fontSize: 24,
            lineHeight: 36,
            textAlign: 'center',
            marginBottom: 24,
          }}>Welcome to Insights Agent</Text>
        </ScrollView>}
        {sendingMessage ? <View style={{
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 10,
        }}>
          <ActivityIndicator size='large' color={Colors.light.hereworksBlue500} />
          <Text
            style={{
              marginTop: 12,
              fontFamily: 'Poppins_400Regular',
              fontSize: 16,
              color: Colors.light.hereworksBlack400,
            }}
          >Thinking...</Text>
        </View> : null}
        {errorMessage ? <View
          testID='chatbot-error-message'
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 10,
            padding: 12,
            backgroundColor: '#FFEEEE',
            marginHorizontal: 20,
            marginBottom: 10,
          }}>
          <Text
            style={{
              fontFamily: 'Poppins_400Regular',
              fontSize: 16,
              color: '#D8000C',
              textAlign: 'center',
            }}
          >{errorMessage}</Text>
        </View> : null}
        <ChatbotTextInput
          placeholder='Type your message here...'
          handleSendMessage={handleSendMessage}
          sendingMessage={sendingMessage}
        />
        {loadingMessages ? <View style={[
          StyleSheet.absoluteFill,
          { justifyContent: 'center', backgroundColor: '#00000005' }]}>
          <ActivityIndicator size='large' color={Colors.light.hereworksBlue500} />
        </View> : null}
        <AlertModal
          modalVisible={deletingSession}
          title={'Deleting chat session'}
          message={'Are you sure you want to delete this chat session? This action cannot be undone.'}
          action={() => {
            deleteChatbotSession(selectedSession.sessionId).then(() => {
              setDeletingSession(false);
              setSelectedSession(null);
              setReloadCount(reloadCount + 1);
            });
          }}
          actionTitle='Delete'
          onModalHide={() => setDeletingSession(false)}
          setModalVisible={setDeletingSession}
        />
      </View>
      {isMobile ? null : <View style={{
        flex: 1,
        backgroundColor: 'white',
        alignSelf: 'stretch',
        borderRadius: 24,
        overflow: 'hidden',
        marginStart: 20,
      }}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: 12 }}
          showsVerticalScrollIndicator
        >
          <Sessions
            title='Chat History'
            icon={<ClockCounterClockwise />}
            sessions={activeSessions}
            loading={loadingActiveSessions}
            expanded={chatHistoryExpanded}
            onToggle={() => setChatHistoryExpanded((prev) => !prev)}
            currentPage={activeSessionsPage}
            totalPages={activeSessionsTotalPages}
            onPageChange={setActiveSessionsPage}
          />
          <Sessions
            title='Saved Sessions'
            icon={<BookmarkIcon />}
            sessions={savedSessions}
            loading={loadingSavedSessions}
            expanded={savedSessionsExpanded}
            onToggle={() => setSavedSessionsExpanded((prev) => !prev)}
            currentPage={savedSessionsPage}
            totalPages={savedSessionsTotalPages}
            onPageChange={setSavedSessionsPage}
          />
          <Sessions
            title='Archived Chats'
            icon={<FontAwesome name="archive" size={16} color={Colors.light.hereworksBlue500} />}
            sessions={archivedSessions}
            loading={loadingArchivedSessions}
            expanded={archivedChatsExpanded}
            onToggle={() => setArchivedChatsExpanded((prev) => !prev)}
            currentPage={archivedSessionsPage}
            totalPages={archivedSessionsTotalPages}
            onPageChange={setArchivedSessionsPage}
          />
        </ScrollView>
      </View>}
      <Modal
        testID='chatbot-history-modal'
        visible={chatHistoryModalVisible}
        onRequestClose={() => setChatHistoryModalVisible(false)}
        animationType="slide"
        presentationStyle={'formSheet'}
      >
        <View style={{ flex: 1, padding: 20, backgroundColor: 'white' }}>
          <View style={[Style.row, { marginBottom: 20 }]}>
            <Text style={{ fontSize: 18, fontWeight: 'bold' }}>Chat History</Text>
            <Pressable
              testID='chatbot-reload-history'
              style={{ marginLeft: 'auto' }}
              onPress={() => {
                setReloadCount(reloadCount + 1);
                setActiveSessionsPage(1);
                setArchivedSessionsPage(1);
              }}>
              <Text style={{
                color: Colors.light.hereworksBlue500,
                fontFamily: 'Poppins_500Medium',
              }}>Reload</Text>
            </Pressable>
          </View>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 12 }}
            showsVerticalScrollIndicator
          >
            <Sessions
              title='Chat History'
              icon={<ClockCounterClockwise />}
              sessions={activeSessions}
              loading={loadingActiveSessions}
              expanded={chatHistoryExpanded}
              onToggle={() => setChatHistoryExpanded((prev) => !prev)}
              currentPage={activeSessionsPage}
              totalPages={activeSessionsTotalPages}
              onPageChange={setActiveSessionsPage}
            />
            <Sessions
              title='Archived Chats'
              icon={<FontAwesome name="archive" size={16} color={Colors.light.hereworksBlue500} />}
              sessions={archivedSessions}
              loading={loadingArchivedSessions}
              expanded={archivedChatsExpanded}
              onToggle={() => setArchivedChatsExpanded((prev) => !prev)}
              currentPage={archivedSessionsPage}
              totalPages={archivedSessionsTotalPages}
              onPageChange={setArchivedSessionsPage}
            />
          </ScrollView>
        </View>
      </Modal>
    </View>
  </KeyboardAvoidingView>;
}
